import { instance } from "@/lib/hooks";
import { createAxiosQuery, createAxiosMutation } from "@/hooks/react-query";

/**
 * The record of a setup link having been issued. Deliberately does not include
 * the link — SupportRent does not store it (see `api/lib/auth0-sso-tickets.ts`).
 */
export interface SsoTicket {
  include_scim: boolean;
  created_at: string;
  created_by: string | null;
}

export interface SsoSetupResponse {
  auth0_connection_name: string | null;
  ticket: SsoTicket | null;
}

/**
 * A POST additionally carries the minted link. It is the only time it is ever
 * sent — a subsequent GET has no `url`, because there is nowhere it was kept.
 */
export interface SsoSetupCreateResponse extends SsoSetupResponse {
  url: string;
}

export const useSsoSetupQuery = createAxiosQuery<
  { organizationId: string | number },
  SsoSetupResponse
>("sso-setup", async (filters) =>
  instance.get<SsoSetupResponse>(`/sso-setup/${filters!.organizationId}`)
);

/**
 * The caller is responsible for the returned `url`: it belongs in component state
 * and on screen, never in a toast or the query cache. The query is invalidated so
 * the status badge flips to `pending`, and its refetch will not carry the link.
 */
export const useSsoSetupMutation = createAxiosMutation<
  SsoSetupCreateResponse,
  unknown,
  {
    organizationId: string | number;
    include_scim: boolean;
  }
>(
  async ({ organizationId, include_scim }) =>
    instance.post<SsoSetupCreateResponse>(`/sso-setup/${organizationId}`, {
      include_scim,
    }),
  {
    onSuccess: (queryClient) => {
      queryClient.invalidateQueries({ queryKey: ["sso-setup"] });
    },
    // No success toast: the link itself is the result and it is already on screen
    // where it can be copied. A toast would be the one piece of UI announcing
    // success while drawing the eye away from the thing that must be copied.
    errorToast: () => ({
      message: "Failed to generate an SSO setup link.",
    }),
  }
);
